<template>
  <tr>
    <td :colspan="bundles.length + 1">
      <b>{{ name }}</b>
    </td>
  </tr>
  <tr>
    <td>{{ isTwoRate ? "Unit Rates" : "Unit Rate" }}</td>
    <td v-for="(bundle, index) in bundles" :key="index">
      <template v-if="isElectricity">
        <template v-if="isTwoRate">
          <div v-if="bundle.elec2Rate?.productDetails.unitRates?.Day">
            <div>
              Day:
              {{
                formatUnitRate(bundle.elec2Rate?.productDetails.unitRates?.Day)
              }}
            </div>
            <div>
              Night:
              {{
                formatUnitRate(
                  bundle.elec2Rate?.productDetails.unitRates?.Night,
                )
              }}
            </div>
          </div>
          <template v-else-if="isMpxn && bundle.elec1Rate">
            {{
              formatUnitRate(
                bundle.elec1Rate?.productDetails.unitRates?.Standard,
              )
            }}
          </template>
          <div v-else>N/A</div>
        </template>
        <template v-else>
          {{
            formatUnitRate(bundle.elec1Rate?.productDetails.unitRates?.Standard)
          }}
        </template>
      </template>
      <template v-else>
        {{ formatUnitRate(bundle.gas?.productDetails.unitRate) }}
      </template>
    </td>
  </tr>
  <tr>
    <td>Standing Charge</td>
    <td v-for="(bundle, index) in bundles" :key="index">
      <template v-if="isElectricity">
        <template v-if="isTwoRate">
          <div
            v-if="
              typeof bundle.elec2Rate?.productDetails.standingCharge ===
              'number'
            "
          >
            {{
              formatStandingCharge(
                bundle.elec2Rate?.productDetails.standingCharge,
              )
            }}
          </div>
          <template v-else-if="isMpxn && bundle.elec1Rate">
            {{
              formatStandingCharge(
                bundle.elec1Rate?.productDetails.standingCharge,
              )
            }}
          </template>
          <div v-else>N/A</div>
        </template>
        <template v-else>
          {{
            formatStandingCharge(
              bundle.elec1Rate?.productDetails.standingCharge,
            )
          }}
        </template>
      </template>
      <template v-else>
        {{ formatStandingCharge(bundle.gas?.productDetails.standingCharge) }}
      </template>
    </td>
  </tr>
  <tr>
    <td>Estimated Annual Usage</td>
    <td v-for="(bundle, index) in bundles" :key="index">
      <template v-if="isElectricity">
        <template v-if="isTwoRate">
          <template v-if="bundle.elec2Rate">
            <div>
              Day:
              {{ usage.electricityDay }}
              kWh
            </div>
            <div>Night: {{ usage.electricityNight }} kWh</div>
          </template>
          <template v-else>
            {{ combinedDayNightUsage.electricity }}
            kWh
          </template>
        </template>
        <template v-else>
          {{
            typeof usage.electricity === "number"
              ? usage.electricity
              : combinedDayNightUsage.electricity
          }}
          kWh
        </template>
      </template>
      <template v-else>{{ usage.gas }} kWh</template>
    </td>
  </tr>
  <tr>
    <td>Estimated Annual Cost</td>
    <td v-for="(bundle, index) in bundles" :key="index">
      <template v-if="isElectricity">
        <template v-if="isTwoRate">
          <template v-if="bundle.elec2Rate">
            {{
              formatMoney({
                amount: annualCost(bundle.elec2Rate, usage),
              })
            }}
          </template>
          <template v-else-if="isMpxn && bundle.elec1Rate">
            {{
              formatMoney({
                amount: annualCost(bundle.elec1Rate, combinedDayNightUsage),
              })
            }}
          </template>
          <template v-else>N/A</template>
        </template>
        <template v-else>
          {{
            bundle?.elec1Rate
              ? formatMoney({
                  amount: annualCost(bundle?.elec1Rate, usage),
                })
              : "N/A"
          }}
        </template>
      </template>
      <template v-else>
        {{
          bundle?.gas
            ? formatMoney({
                amount: annualCost(bundle?.gas, usage),
              })
            : "N/A"
        }}
      </template>
    </td>
  </tr>
  <tr>
    <td>Early Exit Fee</td>
    <td v-for="(bundle, index) in bundles" :key="index">
      {{ formatExitFee(bundle) }}
    </td>
  </tr>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import { formatMoney, formatPriceInPence } from "@/helpers/moneyHelpers"
  import { IUsageEstimation } from "@/services/UsageEstimationService/types"
  import { ITariffBundle } from "@/models/tariffBundle/tariffBundle.types"
  import tariffHelper from "@/helpers/tariffHelper"

  export default defineComponent({
    components: {},
    props: {
      bundles: {
        type: Array as PropType<ITariffBundle[]>,
        required: true,
      },
      usage: {
        type: Object as PropType<Partial<IUsageEstimation>>,
        required: true,
      },
      name: {
        type: String,
        required: true,
      },
      isElectricity: {
        type: Boolean,
        default: false,
      },
      isTwoRate: {
        type: Boolean,
        default: false,
      },
      isMpxn: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        annualCost: tariffHelper.calculateBasicAnnualCost,
        formatMoney,
      }
    },
    computed: {
      combinedDayNightUsage() {
        return {
          electricity:
            (this.usage.electricityDay ?? 0) +
            (this.usage.electricityNight ?? 0),
        }
      },
    },
    methods: {
      formatExitFee(bundle: ITariffBundle) {
        const fee = this.isElectricity
          ? bundle.exitFees.elec
          : bundle.exitFees.gas
        return fee !== undefined ? formatMoney({ amount: fee }) : "N/A"
      },
      formatUnitRate(val?: number) {
        if (typeof val === "number") return `${formatPriceInPence(val)}/kWh`
        else return "N/A"
      },
      formatStandingCharge(val?: number) {
        if (typeof val === "number") return `${formatPriceInPence(val)}/day`
        else return "N/A"
      },
    },
  })
</script>

<style scoped>
  tr,
  td {
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;
    border: 1px solid rgb(var(--v-theme-border));
    font-size: 16px;
    background-color: rgb(var(--v-theme-surface));
  }

  td {
    padding: 4px;
  }
</style>
